// Angular
import {
  ChangeDetectionStrategy,
  Component
} from '@angular/core';
import { CommonModule } from '@angular/common';

// Angular Material
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

// Third party
import { take } from 'rxjs';

// Local library
import {
  EncodedRunnerLeg,
  HelpService,
  ProfileService
} from '@amwager/account/data-access';
import {
  IWagerSelectionSnapshot,
  WagerFacade,
  WagerSelectionService
} from '@amwager/wager/data-access';
import { IGetEventsDetailsResponse } from '@amwager/util-ts';
import { MatNotificationService,
  TranslatePipe
} from '@amwager/util';

/**
 * Help panel inside the profile — support contacts plus quick
 * links (mirrors the Ionic about-settings Help screen and the
 * legacy web Help tab).
 */
@Component({
  selector: 'feature-mat-help',
  templateUrl: './help.component.html',
  styleUrls: ['./help.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    TranslatePipe
  ]
})
export class HelpComponent {
  /** External AmWager features/User Guide page. */
  readonly userGuideUrl = 'https://www.amwager.com/features';

  constructor(
    private readonly profileService: ProfileService,
    private readonly helpService: HelpService,
    private readonly wagerFacade: WagerFacade,
    private readonly wagerSelectionService: WagerSelectionService,
    private readonly matNotificationService: MatNotificationService
  ) { }

  /**
   * Close Help and return the profile panel to its default section
   * @returns {void}
   */
  close(): void {
    this.profileService.setActiveSection('default');
  }

  /**
   * Export today's tracks rules & codes to a CSV download
   * @returns {void}
   */
  downloadRulesAndCodes(): void {
    this.wagerFacade.getSelectAllEventTrackRace$()
      .pipe(take(1))
      .subscribe((events: IGetEventsDetailsResponse[]) => {
        if (!this.helpService.downloadRulesAndCodes(events ?? [])) {
          this.showInfo('No tracks are available to download right now.');
        }
      });
  }

  /**
   * Build a shareable link for the current wager selection and copy it
   * @returns {void}
   */
  generateEncodedRunners(): void {
    const link = this.buildShareLinkFromSelection(this.wagerSelectionService.getSelection());
    if (!link) {
      this.showInfo('Build a wager selection first, then generate its shareable link.');
      return;
    }

    this.helpService.copyToClipboard(link).then((copied: boolean) =>
      this.showInfo(
        copied
          ? 'Wager link copied to clipboard.'
          : 'Unable to copy the wager link.'
      )
    );
  }

  /**
   * Map the current selection's legs into an encoded-runner share link
   * @param {IWagerSelectionSnapshot | null} selection Current selection
   * @returns {string | null} Share link, or null when no valid selection
   */
  private buildShareLinkFromSelection(selection: IWagerSelectionSnapshot | null): string | null {
    // OLD: required selected runners (legs.length), so no link without a bet.
    // if (!selection?.eventId || !selection.poolId || !selection.legs?.length) return null;

    // Event + pool context is enough; runners are optional (empty = no legs).
    if (!selection?.eventId || !selection.poolId) return null;

    const selectionRunners: EncodedRunnerLeg[] = selection.legs.map((leg) => {
      const runners: EncodedRunnerLeg = {};
      (leg?.Runners ?? '')
        .split(',')
        .map((runnerId) => runnerId.trim())
        .filter(Boolean)
        .forEach((runnerId) => (runners[runnerId] = '1'));
      return runners;
    });

    return this.helpService.buildWagerShareLink({
      eventId: selection.eventId,
      race: selection.raceId ?? '',
      poolId: selection.poolId,
      baseAmount: selection.baseAmount ?? '',
      modifier: selection.modifier ?? '',
      selectionRunners
    });
  }

  /**
   * Shared info alert
   * @param {string} message Message to display
   * @returns {void}
   */
  private showInfo(message: string): void {
    this.matNotificationService.showAlert('Info', message, '', '', 'Close');
  }
}
